import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Users, Calendar, User, Shield, Mail, LucideIcon } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { useMessageStore } from "../../store/messageStore";
import { ProfileAvatar, NotificationBell } from "./";

interface NavigationItem {
  name: string;
  href: string;
  icon: LucideIcon;
  current: boolean;
}

const FloatingNavigation: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const { hasUnreadMessages, checkUnreadMessages } = useMessageStore();

  const navigation: NavigationItem[] = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: Users,
      current: location.pathname === "/dashboard",
    },
    {
      name: "Evenementen",
      href: "/events",
      icon: Calendar,
      current: location.pathname.startsWith("/events"),
    },
    {
      name: "Groepen",
      href: "/groups",
      icon: Users,
      current: location.pathname.startsWith("/groups"),
    },
    {
      name: "Profiel",
      href: "/profile",
      icon: User,
      current: location.pathname === "/profile",
    },
    ...(user?.isAdmin ? [{
      name: "Admin",
      href: "/admin",
      icon: Shield,
      current: location.pathname === "/admin",
    }] : []),
  ];

  const handleMailIconClick = () => {
    // When navigating to messages page, we should update the global unread state
    // but not mark individual conversations as read until they're actually opened
    if (location.pathname !== '/messages') {
      // We're navigating TO the messages page, so update unread status
      setTimeout(() => checkUnreadMessages(), 100);
    }
  };

  return (
    <div className="hidden lg:block">
      <nav className="fixed top-0 inset-x-0 z-50 px-5 pt-6">
        <div className="sf-nav mx-auto max-w-[1360px]">
          <div className="flex items-center justify-between gap-4 py-[11px] pl-[18px] pr-3.5">
            <div className="flex items-center gap-7">
              {/* Logo */}
              <Link to="/dashboard" className="flex items-center gap-2.5">
                <div className="sf-logo-mark h-[34px] w-[34px] text-[19px]">
                  S
                </div>
                <span className="text-xl font-extrabold tracking-tight text-fg-bright">
                  SOLOFIX
                </span>
              </Link>

              {/* Navigation Items */}
              <div className="flex items-center gap-0.5">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={
                        item.current ? "sf-nav-item-active" : "sf-nav-item"
                      }
                    >
                      <Icon className="h-4 w-4" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Right side - User elements */}
            <div className="flex items-center gap-2 pr-1.5">
              {/* Messages Icon */}
              <Link
                to="/messages"
                className="relative p-2 text-fg-nav hover:text-fg-bright rounded-xl transition-colors"
                onClick={handleMailIconClick}
              >
                <Mail className="h-5 w-5" />
                {hasUnreadMessages && (
                  <span className="absolute top-0.5 right-0.5 h-2 w-2 bg-neon-pink rounded-full shadow-neon-pink-strong" />
                )}
              </Link>

              {/* Notification Bell */}
              <NotificationBell />

              {/* User Profile Avatar */}
              <Link to="/profile" className="ml-1">
                <ProfileAvatar user={user} size="sm" className="hover:opacity-80 transition-opacity duration-200" />
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default FloatingNavigation;
